/**
 * Canonical JSON-like serialization used by deterministic hashes.
 *
 * Object keys use JavaScript code-unit ordering, undefined object values are
 * omitted, and array order is always preserved. Callers must normalize arrays
 * that represent unordered sets before hashing.
 */
export function canonicalStringify(value: unknown): string {
  if (value === null) {
    return "null";
  }

  if (typeof value === "string" || typeof value === "boolean") {
    return JSON.stringify(value);
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError("Canonical hashing only supports finite numbers");
    }
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalStringify(item)).join(",")}]`;
  }

  if (typeof value === "object") {
    const record = value as Readonly<Record<string, unknown>>;
    const entries = Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalStringify(record[key])}`);
    return `{${entries.join(",")}}`;
  }

  throw new TypeError(`Unsupported value in canonical hash: ${typeof value}`);
}

/**
 * Fate Kingdom's existing FNV-1a 64-bit implementation over JavaScript UTF-16
 * code units. Keep this byte-for-byte compatible with legacy state hashes.
 */
export function fnv1a64Hex(value: string): string {
  let hash = 0xcbf2_9ce4_8422_2325n;
  const prime = 0x0000_0100_0000_01b3n;
  const mask = 0xffff_ffff_ffff_ffffn;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= BigInt(value.charCodeAt(index));
    hash = (hash * prime) & mask;
  }

  return hash.toString(16).padStart(16, "0");
}

export function getCanonicalFnv1a64Hash(value: unknown, prefix = "fnv1a64"): string {
  if (prefix.length === 0) {
    throw new TypeError("Canonical hash prefix cannot be empty");
  }

  return `${prefix}:${fnv1a64Hex(canonicalStringify(value))}`;
}
